import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToastStore } from "@/store/toastStore";
import {
  Box,
  Typography,
  Chip,
  Switch,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Search,
  LocationOn,
  Group,
  LocalShipping,
  MoreVert,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import { zoneService, type Zone } from "@/services/zone.service";
import { hasManualBoundary } from "@/utils/zoneBoundary";

const ZONE_COLORS = [
  "#f27f0d",
  "#33FF57",
  "#3357FF",
  "#F333FF",
  "#FFB533",
  "#33FFF5",
];

type FilterMode = "all" | "active" | "inactive";

/**
 * Page de gestion des zones de livraison
 * Maquette « Zones de livraison »
 */
export function ZonesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    zoneId: string | null;
    zoneName: string;
  }>({ open: false, zoneId: null, zoneName: "" });

  const {
    data: zones = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["zones"],
    queryFn: () => zoneService.getAll(true),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { isActive: boolean } }) =>
      zoneService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
      success("Zone mise à jour");
    },
    onError: (err: unknown) => {
      showError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || "Erreur lors de la mise à jour",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => zoneService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
      success("Zone supprimée avec succès");
      setDeleteConfirm({ open: false, zoneId: null, zoneName: "" });
      setAnchorEl(null);
      setSelectedZone(null);
    },
    onError: (err: unknown) => {
      showError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || "Erreur lors de la suppression",
      );
    },
  });

  const filteredZones = zones.filter((z) => {
    const matchSearch =
      !searchQuery ||
      z.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      z.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      z.geographicSector?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchSearch) return false;
    if (filterMode === "active") return z.isActive;
    if (filterMode === "inactive") return !z.isActive;
    return true;
  });

  const sortedZones = [...filteredZones].sort(
    (a, b) => a.deliveryOrder - b.deliveryOrder || a.zoneNumber - b.zoneNumber,
  );

  const activeCount = zones.filter((z) => z.isActive).length;
  const inactiveCount = zones.filter((z) => !z.isActive).length;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, zone: Zone) => {
    setAnchorEl(e.currentTarget);
    setSelectedZone(zone);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedZone(null);
  };

  const handleEdit = () => {
    if (selectedZone) navigate(`/zones/${selectedZone.id}/edit`);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (selectedZone) {
      setDeleteConfirm({
        open: true,
        zoneId: selectedZone.id,
        zoneName: selectedZone.name,
      });
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.zoneId) deleteMutation.mutate(deleteConfirm.zoneId);
  };

  const handleToggleActive = (zone: Zone) => {
    updateMutation.mutate({ id: zone.id, data: { isActive: !zone.isActive } });
  };

  if (isLoading) return <LoadingSpinner message="Chargement des zones..." />;
  if (queryError)
    return (
      <ErrorMessage
        title="Erreur de chargement"
        message="Erreur lors du chargement des zones."
        onRetry={() => queryClient.invalidateQueries({ queryKey: ["zones"] })}
        retryLabel="Réessayer"
      />
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Zones de livraison"
        subtitle="Gérez les zones de livraison actives à Cayenne et ses environs."
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/zones/new")}
          >
            Ajouter une zone
          </Button>
        }
      />

      <FiltersCard>
        <InputField
          placeholder="Rechercher une zone (ex: Centre, Kourou)..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={<Search />}
          iconPosition="start"
          sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label="Toutes"
            onClick={() => setFilterMode("all")}
            color={filterMode === "all" ? "primary" : "default"}
            variant={filterMode === "all" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Actives (${activeCount})`}
            onClick={() => setFilterMode("active")}
            color={filterMode === "active" ? "primary" : "default"}
            variant={filterMode === "active" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Inactives (${inactiveCount})`}
            onClick={() => setFilterMode("inactive")}
            color={filterMode === "inactive" ? "primary" : "default"}
            variant={filterMode === "inactive" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </FiltersCard>

      {sortedZones.length === 0 ? (
        <EmptyState
          title="Aucune zone trouvée"
          description={
            searchQuery || filterMode !== "all"
              ? "Aucune zone ne correspond aux filtres."
              : "Créez une première zone de livraison."
          }
          action={
            !searchQuery && filterMode === "all"
              ? { label: "Ajouter une zone", onClick: () => navigate("/zones/new") }
              : undefined
          }
        />
      ) : (
        <Grid container spacing={3}>
          {sortedZones.map((zone, idx) => (
            <Grid item xs={12} sm={6} lg={4} key={zone.id}>
              <Card
                sx={{
                  p: 2.5,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  overflow: "hidden",
                  opacity: zone.isActive ? 1 : 0.85,
                  "&:hover": { boxShadow: 3 },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    bgcolor: ZONE_COLORS[idx % ZONE_COLORS.length],
                  }}
                />
                <Box sx={{ pl: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="h3" sx={{ fontSize: "1.125rem", fontWeight: 700 }}>
                          {zone.name}
                        </Typography>
                        <Chip
                          label={zone.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={zone.isActive ? "success" : "default"}
                          variant="filled"
                          sx={{ fontSize: "0.7rem", height: 22 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {zone.geographicSector || zone.description || "—"}
                      </Typography>
                      {zone.deliveryCost != null && Number(zone.deliveryCost) > 0 && (
                        <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ display: "block", mt: 0.25 }}>
                          Forfait livraison (par commande) : {Number(zone.deliveryCost).toFixed(2)} €
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        {hasManualBoundary(zone.boundary)
                          ? "Périmètre dessiné"
                          : zone.radiusKm
                            ? "Rayon à redessiner"
                            : "Périmètre à définir"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: `${ZONE_COLORS[idx % ZONE_COLORS.length]}20`,
                          color: ZONE_COLORS[idx % ZONE_COLORS.length],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LocationOn sx={{ fontSize: 20 }} />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, zone)}
                        aria-label="Actions"
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                          <Group sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Adresses
                          </Typography>
                        </Box>
                        <Typography variant="h4" sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
                          {zone._count?.customerAddresses ?? 0}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                          <LocalShipping sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Livraisons
                          </Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "1.25rem",
                            fontWeight: 700,
                            color: (zone._count?.deliveries ?? 0) > 0 ? "primary.main" : "text.secondary",
                          }}
                        >
                          {zone._count?.deliveries ?? 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Statut du secteur
                    </Typography>
                    <Switch
                      checked={zone.isActive}
                      onChange={() => handleToggleActive(zone)}
                      disabled={updateMutation.isPending}
                      color="primary"
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}
      >
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1, fontSize: 20 }} /> Modifier
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <Delete sx={{ mr: 1, fontSize: 20 }} /> Supprimer
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Supprimer la zone"
        message={`Êtes-vous sûr de vouloir supprimer la zone « ${deleteConfirm.zoneName } » ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ open: false, zoneId: null, zoneName: "" })}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
